import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";

import { AuthenticationScreenContainer, useToastStore } from "@/components";
import { useAuth } from "@/hooks";
import { Button, EmailInput, IconButton, icons } from "@/ui";

const schema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

type ForgotPasswordFormData = z.infer<typeof schema>;

const ForgotPassword = () => {
  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(schema),
  });

  const { isPending, mutate: requestResetPasswordMutation } = useAuth().useRequestResetPassword();

  const submitEmail = (data: ForgotPasswordFormData) => {
    requestResetPasswordMutation(data, {
      onSuccess: async (data) => {
        await pushToast({
          type: "success",
          message: data.data.message,
        });
      },
    });
  };

  return (
    <AuthenticationScreenContainer className="flex flex-col gap-6">
      <header className="flex w-full flex-row items-center gap-1">
        <IconButton className="-ml-2" label="Back to login" size="sm" asChild>
          <Link to="/login">
            <icons.ChevronLeftIcon aria-hidden="true" className="size-5" />
          </Link>
        </IconButton>

        <h1 className="text-xl font-bold">Forgot password?</h1>
      </header>

      <p className="text-content-secondary">
        No problem! We’ll just need the email associated with your account, and then we’ll send you
        a confirmation code in order to reset your password.
      </p>

      <form className="flex w-full flex-col gap-5" onSubmit={handleSubmit(submitEmail)}>
        <EmailInput
          autoComplete="email"
          className="h-14"
          error={errors.email?.message}
          id="email"
          label="Email"
          placeholder="you@example.com"
          reset={() => {
            return reset();
          }}
          {...register("email")}
        />

        <Button className="w-full" isLoading={isPending} size="lg" type="submit" variant="primary">
          Send password reset email
        </Button>
      </form>
    </AuthenticationScreenContainer>
  );
};

export const Route = createFileRoute("/_guest/forgot-password/")({ component: ForgotPassword });
